// Session-gated client for the S5 Setup (install, settings) and S7 Support
// screens — US-130..133.
//
// Same auth model as `screens/commerce/api.ts`: `GET/PUT /licenses*` and
// `POST /support` are cookie-authenticated (`require_session`, the human
// credential), so every request carries `credentials: "same-origin"` and no key
// header. This is deliberately NOT `dashboard/api.ts`'s `X-mAiSight-Key`
// pattern — that one is the machine credential for the pre-PRD-18 dashboard.
//
// `same-origin`, NOT `include`, and the difference is not cosmetic — this
// matches `screens/overview/api.ts`, which had it right first. The API sets
// `allow_credentials=False` (`backend/api/app.py`), so a cross-origin fetch
// carrying `include` is rejected by the browser before the response is read and
// every panel goes dark; `same-origin` simply omits the cookie and the screen
// renders its signed-out state. Deployed (and under `scripts/gate-demo.sh`) the
// SPA reaches the API same-origin, so the session cookie rides as intended.
//
// Response/request shapes are pinned to setup-backend's Wave 1 handoff
// verbatim (PRDs/18-figma-surfaces/progress/progress-setup-backend.txt).
// `dashboard/api.ts` and `screens/commerce/api.ts` are other agents' fences
// this wave, so their private helpers could not be imported and are mirrored
// below — the same disclosed duplication `screens/analytics/api.ts` already
// notes. `polish` should collapse these into one client.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";

const API_BASE =
  (import.meta.env?.VITE_API_BASE as string | undefined) ??
  "http://localhost:8000";

export type ResolvedLicenseState = "trial" | "active" | "expired" | "unknown";
export type Plan = "trial" | "pro" | "scale" | "enterprise" | null;

export interface LicenseRecord {
  org_slug: string;
  plan: Plan;
  domains: string[];
  status: "active" | "canceled" | null;
  contact_email: string | null;
  resolved_state: ResolvedLicenseState;
  /** Only meaningful when resolved_state === "trial". */
  days_remaining: number | null;
  /** Naive-UTC, no offset/Z suffix. Null for a paid plan or no license row. */
  expires_at: string | null;
  created_at: string | null;
}

export interface LicenseUsage {
  org_slug: string;
  window_days: number;
  document_count: number;
  term_count: number;
  /** Null iff cost_unknown is true — never fabricate a number here. */
  total_cost_usd: number | null;
  cost_unknown: boolean;
  try_spend_today_usd: number;
  try_daily_cap_usd: number;
}

/**
 * One license key described WITHOUT the key. `key_id` is the backend's
 * `fingerprint(key_hash)` — the first 12 hex chars of the sha256, safe to
 * render and to put in a URL. There is no field here that could ever hold a
 * secret, which is the point: only sha256(key) is stored, so no read can
 * return one. `IssuedKey.key` below is the single exception and exists for
 * exactly one response.
 */
export interface LicenseKey {
  key_id: string;
  plan: Exclude<Plan, null>;
  domains: string[];
  status: "active" | "canceled";
  resolved_state: ResolvedLicenseState;
  expires_at: string | null;
  created_at: string | null;
}

export interface IssuedKey {
  /** Shown once, never persisted, never refetchable. */
  key: string;
  license: LicenseKey;
}

/**
 * The activation check (`GET /install/check`, PRD 17 §3).
 *
 * Three verdicts, and only ONE of them says anything about the install:
 * `reporting` confirms it works. `quiet` and `never_reported` are statements
 * about TRAFFIC — a correctly installed widget on a page nobody has visited
 * emits nothing, so neither may ever be rendered as a failure. `detail` is the
 * backend's own sentence for exactly that reason; prefer it over composing one
 * here.
 *
 * There is deliberately no domain verdict: events carry no origin, so a wrong
 * domain and an unvisited page are the same observable. `bound_domains` is
 * configuration and `domain_check_reason` says why nothing is checked.
 */
export interface InstallCheck {
  org_slug: string;
  window_hours: number;
  /** Naive-UTC, no offset/Z suffix — same convention as `expires_at`. */
  checked_at: string;
  events_in_window: number;
  documents_in_window: number;
  /** Server arrival time of the most recent event at ANY time. Null only when
   *  nothing has ever arrived. */
  last_event_at: string | null;
  verdict: "reporting" | "quiet" | "never_reported";
  detail: string;
  bound_domains: string[];
  domain_check_reason: string;
}

export interface ReportPrefsResult {
  org_slug: string;
  contact_email: string | null;
  subscribed: boolean;
}

export interface SupportTicketResult {
  ticket_id: string;
  org_slug: string;
  status: string;
}

export interface Me {
  email: string;
  org_slug: string;
}

/** RFC-7807 `application/problem+json` (`backend/api/errors.py`). */
export type ProblemDetail = {
  type: string;
  title: string;
  status: number;
  detail?: string | null;
};

export class OpsApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetail | null;

  constructor(status: number, problem: ProblemDetail | null, fallback: string) {
    super(problem?.detail ?? problem?.title ?? fallback);
    this.status = status;
    this.problem = problem;
  }
}

function endpoint(path: string): string {
  return new URL(
    `${API_BASE}${path}`,
    typeof window !== "undefined" ? window.location.origin : undefined,
  ).toString();
}

async function readProblem(res: Response): Promise<ProblemDetail | null> {
  try {
    const body = (await res.json()) as Partial<ProblemDetail>;
    if (typeof body.title === "string") return body as ProblemDetail;
    return null;
  } catch {
    return null; // Non-JSON body (network failure, proxy error page, ...).
  }
}

async function getJSON<T>(path: string): Promise<T> {
  const res = await fetch(endpoint(path), {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    throw new OpsApiError(res.status, await readProblem(res), `GET ${path} failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

async function sendJSON<T>(path: string, method: "POST" | "PUT", body: unknown): Promise<T> {
  const res = await fetch(endpoint(path), {
    method,
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new OpsApiError(res.status, await readProblem(res), `${method} ${path} failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

export function useMe(): UseQueryResult<Me | null, Error> {
  return useQuery<Me | null, Error>({
    queryKey: ["ops-auth-me"],
    queryFn: async () => {
      const res = await fetch(endpoint("/auth/me"), {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });
      if (res.status === 401) return null;
      if (!res.ok) throw new Error(`GET /auth/me failed: ${res.status}`);
      return (await res.json()) as Me;
    },
  });
}

export function useLicense(): UseQueryResult<LicenseRecord, Error> {
  return useQuery<LicenseRecord, Error>({
    queryKey: ["ops-license"],
    queryFn: () => getJSON<LicenseRecord>("/licenses"),
  });
}

/**
 * The real "verify it's live" request — `refetch()` on the returned query is
 * an actual round trip, never an optimistic message.
 *
 * Reads raw `events` server-side rather than the rollups, on purpose: the
 * aggregator ticks every five minutes and `doc_metrics.date` is a DATE, so a
 * rollup-backed check would tell a customer who installed sixty seconds ago
 * that we have seen nothing. See `backend/analytics/install_check.py`.
 */
export function useInstallCheck(hours = 24): UseQueryResult<InstallCheck, Error> {
  return useQuery<InstallCheck, Error>({
    queryKey: ["ops-install-check", hours],
    queryFn: () => getJSON<InstallCheck>(`/install/check?hours=${hours}`),
  });
}

/** C1 — what arrived, of which kind, over the last N hours.
 *
 * Same exemption and the same two reasons as `useInstallCheck`: a rollup ticks
 * every five minutes and its `date` is a DATE, so neither freshness nor an
 * hourly shape is expressible by one. `backend/analytics/arrivals.py` carries
 * the argument.
 *
 * **An org that has never reported comes back with `points: []`, not a series
 * of zeros.** A caller must not draw one — a correctly installed widget on a
 * page nobody has visited emits nothing at all, and a chart of zero-height
 * bars states the opposite.
 */
export interface ArrivalPoint {
  /** Naive-UTC, truncated to the bucket. */
  bucket: string;
  type: string;
  events: number;
}

export interface ArrivalSeries {
  org_slug: string;
  window_hours: number;
  bucket: "hour" | "day";
  checked_at: string;
  points: ArrivalPoint[];
  /** Stable draw order for the types present, so a stacked series keeps its
   *  colours between refreshes instead of following dictionary order. */
  types: string[];
  total_events: number;
}

export function useArrivals(
  hours = 168,
  bucket: "hour" | "day" = "day",
): UseQueryResult<ArrivalSeries, Error> {
  return useQuery<ArrivalSeries, Error>({
    queryKey: ["ops-arrivals", hours, bucket],
    queryFn: () =>
      getJSON<ArrivalSeries>(`/install/arrivals?hours=${hours}&bucket=${bucket}`),
  });
}

export function useLicenseUsage(days = 30): UseQueryResult<LicenseUsage, Error> {
  return useQuery<LicenseUsage, Error>({
    queryKey: ["ops-license-usage", days],
    queryFn: () => getJSON<LicenseUsage>(`/licenses/usage?days=${days}`),
  });
}

export function useUpdateReportPrefs() {
  const queryClient = useQueryClient();
  return useMutation<ReportPrefsResult, OpsApiError, string | null>({
    mutationFn: (contactEmail) =>
      sendJSON<ReportPrefsResult>("/licenses/report-prefs", "PUT", {
        contact_email: contactEmail,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["ops-license"] });
    },
  });
}

const KEYS_QUERY = ["ops-license-keys"];

export function useLicenseKeys(): UseQueryResult<LicenseKey[], Error> {
  return useQuery<LicenseKey[], Error>({
    queryKey: KEYS_QUERY,
    queryFn: () => getJSON<LicenseKey[]>("/licenses/keys"),
  });
}

/**
 * Issue a replacement key. The mutation result carries the plaintext and is
 * the ONLY place it exists — it is deliberately not written to the query
 * cache, so a refetch, a remount, or a navigation loses it, which is exactly
 * what "we cannot show this to you again" has to mean.
 */
export function useMintLicenseKey() {
  const queryClient = useQueryClient();
  return useMutation<IssuedKey, OpsApiError, void>({
    mutationFn: () => sendJSON<IssuedKey>("/licenses/keys", "POST", {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: KEYS_QUERY });
    },
  });
}

export function useRevokeLicenseKey() {
  const queryClient = useQueryClient();
  return useMutation<LicenseKey, OpsApiError, string>({
    mutationFn: (keyId) =>
      sendJSON<LicenseKey>(`/licenses/keys/${encodeURIComponent(keyId)}/revoke`, "POST", {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: KEYS_QUERY });
      void queryClient.invalidateQueries({ queryKey: ["ops-license"] });
    },
  });
}

export interface SubmitSupportInput {
  subject: string;
  body: string;
}

export function useSubmitSupport() {
  return useMutation<SupportTicketResult, OpsApiError, SubmitSupportInput>({
    mutationFn: (input) => sendJSON<SupportTicketResult>("/support", "POST", input),
  });
}
